import MainLayout from "../layouts/MainLayout";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";

export default function HomePage() {
  return (
    <MainLayout>
      <SEO 
        title="Eyad Kurd-Misto | Data Scientist & Researcher"
        description="Eyad Kurd-Misto, Ph.D. — data scientist combining deep mathematical and statistical foundations with hands-on ML engineering: forecasting, NLP, LLM and agentic systems, and end-to-end data pipelines."
      />
      
      {/* Hero section with split screen layout */}
      <div className="mb-10 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-lg overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Image Section - Left 40% */}
          <div className="lg:w-2/5 flex items-center justify-center p-8">
            <div className="relative">
              <div className="w-64 h-64 lg:w-80 lg:h-80 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full absolute -inset-1 blur-sm opacity-50"></div>
              <img
                src="/pics/Eyad Professional.jpg"
                alt="Eyad Kurd-Misto"
                className="rounded-full w-64 h-64 lg:w-80 lg:h-80 object-cover object-top border-4 border-blue-500 shadow-2xl relative z-10"
              />
            </div>
          </div>
          
          {/* Content Section - Right 60% */}
          <div className="lg:w-3/5 p-8 flex flex-col justify-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-gradient bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                Eyad Kurd-Misto, Ph.D.
              </h1>
            <p className="text-2xl text-gray-300 mb-6">
                Data Scientist
              </p>
            
            <div className="bg-gray-800 bg-opacity-40 p-6 rounded-lg border border-gray-700">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">Core Expertise</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-900 bg-opacity-50 rounded text-sm border border-blue-700">LLM Integration</span>
                <span className="px-3 py-1 bg-purple-900 bg-opacity-50 rounded text-sm border border-purple-700">Agentic Systems</span>
                <span className="px-3 py-1 bg-green-900 bg-opacity-50 rounded text-sm border border-green-700">Machine Learning</span>
                <span className="px-3 py-1 bg-blue-900 bg-opacity-50 rounded text-sm border border-blue-700">Statistical & Probabilistic Modeling</span>
                <span className="px-3 py-1 bg-purple-900 bg-opacity-50 rounded text-sm border border-purple-700">Data Pipeline Design</span>
                <span className="px-3 py-1 bg-green-900 bg-opacity-50 rounded text-sm border border-green-700">Forecasting Models</span>
                <span className="px-3 py-1 bg-blue-900 bg-opacity-50 rounded text-sm border border-blue-700">Mathematical Modeling</span>
                <span className="px-3 py-1 bg-purple-900 bg-opacity-50 rounded text-sm border border-purple-700">Computational Theory</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-semibold mb-4">Welcome</h2>
            <p className="text-lg">
              I'm a Data Scientist with a Ph.D. in Computational and Data Sciences. At ALEKS McGraw Hill, I work on experimental research projects to improve personalized learning systems, focusing on LLM integration and agentic systems, machine learning, and data pipeline design.
            </p>
            <p className="mt-4 text-lg">
              My doctoral research focused on probabilistic extensions to functional programming languages, combining category theory, measure theory, and programming language theory. I bring both theoretical depth and practical implementation skills to build robust machine learning systems.
            </p>
          </div>
          
          <div className="bg-gray-800 bg-opacity-40 p-6 rounded-lg border border-gray-700">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">Highlights</h3>
            <ul className="space-y-3">
              <li className="flex">
                <div className="mr-2 mt-1 shrink-0">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                </div>
                <span className="text-sm">Senior Applied Research Data Scientist at ALEKS McGraw Hill</span>
              </li>
              <li className="flex">
                <div className="mr-2 mt-1 shrink-0">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                </div>
                <span className="text-sm">Ph.D. in Computational & Data Sciences (Chapman University)</span>
              </li>
              <li className="flex">
                <div className="mr-2 mt-1 shrink-0">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
                <span className="text-sm">Research in probabilistic programming languages</span>
              </li>
              <li className="flex">
                <div className="mr-2 mt-1 shrink-0">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                </div>
                <span className="text-sm">Expertise in agent design and ML pipeline development</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Quick links section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/projects#dissertation" className="bg-gray-800 p-4 rounded-lg hover:bg-gray-750 transition-colors border border-gray-700 hover:border-blue-500">
            <h3 className="font-semibold text-blue-400 mb-2">Research</h3>
            <p className="text-sm text-gray-300">Explore my dissertation on Random Variable Spaces alongside selected publications.</p>
          </Link>
          <Link to="/projects" className="bg-gray-800 p-4 rounded-lg hover:bg-gray-750 transition-colors border border-gray-700 hover:border-purple-500">
            <h3 className="font-semibold text-purple-400 mb-2">Professional Projects</h3>
            <p className="text-sm text-gray-300">Discover my work in forecasting models, dashboards, and data pipeline design.</p>
          </Link>
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 hover:border-green-500 transition-colors">
            <h3 className="font-semibold text-green-400 mb-3">Contact</h3>
                         <div className="space-y-2 text-sm">
               <div className="flex items-center">
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                 </svg>
                 <a href="mailto:ekurdmisto@gmail.com" className="text-blue-400 hover:underline">
                   ekurdmisto@gmail.com
                 </a>
               </div>
               <div className="flex items-center">
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                 </svg>
                 <a href="tel:+19512881563" className="text-blue-400 hover:underline">
                   +1 (951) 288-1563
                 </a>
               </div>
               <div className="flex items-center">
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                 </svg>
                 <span className="text-gray-300">Riverside, CA</span>
               </div>
             </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
} 